import React, { useState, useEffect, useMemo } from "react";
import { Grid, Card } from "tabler-react";
import SiteWrapper from "../../../SiteWrapper.react";
import Crud_Catalogos from "../../../herramientas/Crud_Catalogos";
import Chart from "react-apexcharts";
import TablePageable from "../../../Helpers/TablePageable.react";
import TableSkeleton from "../../../components/TableSkeleton/TableSkeleton";

function getSessionData(key) {
  try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; }
}

// Conteos por rango de días, global y por transportista, de la lista recibida.
function calcularResumen(lista) {
  const porDias = { verde: 0, amarillo: 0, rojo: 0, gris: 0 };
  const porTransportista = {};

  lista.forEach(gps => {
    if (gps.color === "green") porDias.verde++;
    else if (gps.color === "yellow") porDias.amarillo++;
    else if (gps.color === "red") porDias.rojo++;
    else porDias.gris++;

    const trans = gps.transportista || "Sin transportista";
    if (!porTransportista[trans]) porTransportista[trans] = { total: 0, verde: 0, amarillo: 0, rojo: 0, gris: 0 };
    porTransportista[trans].total++;
    if (gps.color === "green") porTransportista[trans].verde++;
    else if (gps.color === "yellow") porTransportista[trans].amarillo++;
    else if (gps.color === "red") porTransportista[trans].rojo++;
    else porTransportista[trans].gris++;
  });

  const transportistasFiltrados = Object.entries(porTransportista).sort((a, b) => b[1].total - a[1].total);

  return { porDias, transportistasFiltrados, total: lista.length };
}

const GpsPorRecuperarPage = () => {
  const [gpsList, setGpsList] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroColor, setFiltroColor] = useState(null);
  const [filtroLocalidad, setFiltroLocalidad] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  useEffect(() => {
    cargarGps();
  }, []);

  const cargarGps = () => {
    const plataforma = getSessionData("plataforma");
    if (!plataforma || !plataforma.clave) { setCargando(false); return; }

    Crud_Catalogos("gps/dash", "", "get", "", [], "", "", "", [])
      .then((data) => {
        const porRecuperar = (Array.isArray(data) ? data : []).filter(item =>
          item.gpsEstatus === "Por Recuperar" && !item.gpsFijo && item.plataforma && 
          (item.plataforma.toUpperCase() === plataforma.clave.toUpperCase() || item.plataforma.toUpperCase() === plataforma.nombre.toUpperCase())
        );
        const fechaActual = new Date().getTime();
        const lista = porRecuperar.map(item => {
          let diasSinRecuperar = null;
          let color = null;
          const raw = item.endDateFormatted || item.endDate;
          if (raw) {
            const fecha = new Date(raw).getTime();
            if (!isNaN(fecha)) {
              diasSinRecuperar = Math.round((fechaActual - fecha) / (1000 * 60 * 60 * 24));
              if (diasSinRecuperar <= 2) color = "green";
              else if (diasSinRecuperar < 10) color = "yellow";
              else color = "red";
            }
          }
          if (diasSinRecuperar === null) color = "gray";
          return { ...item, diasSinRecuperar, color };
        });
        setGpsList(lista.sort((a, b) => (b.diasSinRecuperar ?? -1) - (a.diasSinRecuperar ?? -1)));
        setCargando(false);
      })
      .catch(() => setCargando(false));
  };

  // Plataforma de la sesión (no cambia durante la sesión).
  const plataformaSesion = useMemo(() => getSessionData("plataforma"), []);
  // Si la plataforma es la 1, la columna "PO" se muestra como "FRO".
  const labelPo = (plataformaSesion && String(plataformaSesion.id) === "1") ? "FRO" : "PO";

  // Localidades (corredor) únicas presentes en los datos, para poblar el select.
  // El endpoint gps/dash no trae idLocalidad; el campo geográfico disponible es "corredor".
  const localidades = useMemo(() => {
    const set = new Set();
    gpsList.forEach(gps => {
      const loc = gps.corredor;
      if (loc) set.add(loc);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [gpsList]);

  // Lista con filtros de localidad + rango de fechas (base de las tarjetas de resumen)
  const listaPorCorredorFecha = useMemo(() => {
    // El rango de fechas se compara contra Fin de Movimiento (endDate/endDateFormatted)
    const desde = fechaDesde ? new Date(fechaDesde + "T00:00:00").getTime() : null;
    const hasta = fechaHasta ? new Date(fechaHasta + "T23:59:59").getTime() : null;

    return gpsList.filter(gps => {
      if (filtroLocalidad && (gps.corredor || "") !== filtroLocalidad) return false;

      if (desde !== null || hasta !== null) {
        const raw = gps.endDateFormatted || gps.endDate;
        const t = raw ? new Date(raw).getTime() : NaN;
        if (isNaN(t)) return false; // sin fecha válida se excluye cuando hay filtro de fechas
        if (desde !== null && t < desde) return false;
        if (hasta !== null && t > hasta) return false;
      }

      return true;
    });
  }, [gpsList, filtroLocalidad, fechaDesde, fechaHasta]);

  // Lista final aplicada a la tabla y a las gráficas: agrega el filtro de color
  const listaFiltrada = useMemo(
    () => (filtroColor ? listaPorCorredorFecha.filter(gps => gps.color === filtroColor) : listaPorCorredorFecha),
    [listaPorCorredorFecha, filtroColor]
  );

  // Las tarjetas no usan el filtro de color para poder seguir viendo y cambiando entre colores
  const resumenTarjetas = useMemo(() => calcularResumen(listaPorCorredorFecha), [listaPorCorredorFecha]);
  const datosCalculados = useMemo(() => calcularResumen(listaFiltrada), [listaFiltrada]);

  return (
    <SiteWrapper>
      <Grid.Row>
        <Grid.Col width={12}>
          <Card>
            <Card.Body>
              {cargando ? (
                <TableSkeleton headers={["Días", "GPS", "IMEI", "Transportista", "Plataforma"]} title="GPS Por Recuperar" rows={8} />
              ) : gpsList.length === 0 ? (
                <div className="text-center text-muted py-5"><p>No hay GPS pendientes por recuperar.</p></div>
              ) : (
                <>
                  <h5 className="text-center mb-4">GPS Por Recuperar ({resumenTarjetas.total})</h5>
                  {/* Resumen */}
                  <div className="row text-center mb-4">
                    <div className="col-md-3" style={{ cursor: "pointer", opacity: filtroColor && filtroColor !== "green" ? 0.4 : 1 }} onClick={() => setFiltroColor(filtroColor === "green" ? null : "green")}>
                      <div style={{ border: "2px solid #28a745", borderRadius: 8, padding: "10px 0", backgroundColor: filtroColor === "green" ? "#e8f5e9" : "transparent" }}>
                        <h3 style={{ color: "#28a745", margin: 0 }}>{resumenTarjetas.porDias.verde}</h3>
                        <p style={{ margin: 0 }}>0-2 días</p>
                      </div>
                    </div>
                    <div className="col-md-3" style={{ cursor: "pointer", opacity: filtroColor && filtroColor !== "yellow" ? 0.4 : 1 }} onClick={() => setFiltroColor(filtroColor === "yellow" ? null : "yellow")}>
                      <div style={{ border: "2px solid #ffc107", borderRadius: 8, padding: "10px 0", backgroundColor: filtroColor === "yellow" ? "#fff8e1" : "transparent" }}>
                        <h3 style={{ color: "#ffc107", margin: 0 }}>{resumenTarjetas.porDias.amarillo}</h3>
                        <p style={{ margin: 0 }}>3-9 días</p>
                      </div>
                    </div>
                    <div className="col-md-3" style={{ cursor: "pointer", opacity: filtroColor && filtroColor !== "red" ? 0.4 : 1 }} onClick={() => setFiltroColor(filtroColor === "red" ? null : "red")}>
                      <div style={{ border: "2px solid #dc3545", borderRadius: 8, padding: "10px 0", backgroundColor: filtroColor === "red" ? "#fce4ec" : "transparent" }}>
                        <h3 style={{ color: "#dc3545", margin: 0 }}>{resumenTarjetas.porDias.rojo}</h3>
                        <p style={{ margin: 0 }}>10+ días</p>
                      </div>
                    </div>
                    <div className="col-md-3" style={{ cursor: "pointer", opacity: filtroColor && filtroColor !== "gray" ? 0.4 : 1 }} onClick={() => setFiltroColor(filtroColor === "gray" ? null : "gray")}>
                      <div style={{ border: "2px solid #6c757d", borderRadius: 8, padding: "10px 0", backgroundColor: filtroColor === "gray" ? "#eceff1" : "transparent" }}>
                        <h3 style={{ color: "#6c757d", margin: 0 }}>{resumenTarjetas.porDias.gris}</h3>
                        <p style={{ margin: 0 }}>Sin fecha</p>
                      </div>
                    </div>
                  </div>

                  {/* Gráficas */}
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <h6 className="text-center">Distribución por días</h6>
                      {datosCalculados.total > 0 ? (
                        <Chart
                          type="donut"
                          height={250}
                          series={[datosCalculados.porDias.verde, datosCalculados.porDias.amarillo, datosCalculados.porDias.rojo, datosCalculados.porDias.gris]}
                          options={{
                            labels: ["0-2 días", "3-9 días", "10+ días", "Sin fecha"],
                            colors: ["#28a745", "#ffc107", "#dc3545", "#6c757d"],
                            legend: { position: "bottom" },
                            dataLabels: { enabled: true, formatter: (val) => val.toFixed(0) + "%" }
                          }}
                        />
                      ) : (
                        <p className="text-center text-muted">Sin datos</p>
                      )}
                    </div>
                    <div className="col-md-8">
                      {datosCalculados.transportistasFiltrados.length > 0 ? (
                        <Chart
                          type="bar"
                          height={Math.max(250, datosCalculados.transportistasFiltrados.length * 35)}
                          series={[
                            { name: "0-2 días", data: datosCalculados.transportistasFiltrados.map(([, d]) => d.verde) },
                            { name: "3-10 días", data: datosCalculados.transportistasFiltrados.map(([, d]) => d.amarillo) },
                            { name: "Más de 10 días", data: datosCalculados.transportistasFiltrados.map(([, d]) => d.rojo) },
                            { name: "Sin fecha", data: datosCalculados.transportistasFiltrados.map(([, d]) => d.gris) }
                          ]}
                          options={{
                            chart: { type: "bar", stacked: true, toolbar: { show: false } },
                            plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
                            dataLabels: {
                              enabled: true,
                              formatter: (val, opts) => opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex] || ""
                            },
                            xaxis: { categories: datosCalculados.transportistasFiltrados.map(([n, d]) => n + " (" + d.total + ")") },
                            yaxis: { labels: { style: { fontSize: "10px" }, maxWidth: 200 } },
                            colors: ["#28a745", "#ffc107", "#dc3545", "#6c757d"],
                            legend: { position: "bottom" },
                            tooltip: {
                              y: { formatter: (val, { dataPointIndex }) => val + " GPS (Total: " + datosCalculados.transportistasFiltrados[dataPointIndex][1].total + ")" }
                            }
                          }}
                        />
                      ) : (
                        <p className="text-center text-muted">Sin datos</p>
                      )}
                    </div>
                  </div>

                  {/* Filtros de tabla */}
                  <div className="row align-items-end mb-3">
                    <div className="col-md-3 mb-2">
                      <label style={{ fontSize: 12, fontWeight: "bold", marginBottom: 2 }}>Corredor</label>
                      <select
                        className="form-control form-control-sm"
                        value={filtroLocalidad}
                        onChange={(e) => setFiltroLocalidad(e.target.value)}
                      >
                        <option value="">Todas</option>
                        {localidades.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3 mb-2">
                      <label style={{ fontSize: 12, fontWeight: "bold", marginBottom: 2 }}>Fin de mov. desde</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fechaDesde}
                        max={fechaHasta || undefined}
                        onChange={(e) => setFechaDesde(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label style={{ fontSize: 12, fontWeight: "bold", marginBottom: 2 }}>Fin de mov. hasta</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fechaHasta}
                        min={fechaDesde || undefined}
                        onChange={(e) => setFechaHasta(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm btn-block"
                        onClick={() => { setFiltroLocalidad(""); setFechaDesde(""); setFechaHasta(""); setFiltroColor(null); }}
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  </div>

                  {/* Tabla detalle */}
                  <TablePageable
                    lista={listaFiltrada}
                    columnas={[
                      { columna: "diasSinRecuperar", label: "Días sin Recuperar", form: (item) => {
                        const dias = item.diasSinRecuperar;
                        if (dias === null) return <span style={{ color: "#6c757d" }}>Sin fecha</span>;
                        let bg = "#28a745";
                        if (dias > 10) bg = "#dc2626";
                        else if (dias > 2) bg = "#ffc107";
                        return <span style={{ background: bg, color: "#fff", padding: "3px 8px", borderRadius: 12, fontWeight: "bold", fontSize: 12 }}>{dias} días</span>;
                      }},
                      { columna: "gps", label: "GPS" },
                      { columna: "imei", label: "IMEI" },
                      { columna: "transportista", label: "Transportista" },
                      { columna: "gpsEstatus", label: "Estatus" },
                      { columna: "plataforma", label: "Plataforma", form: (item) => item.plataforma || item.idPlataforma?.nombre || "N/A" },
                      { columna: "corredor", label: "Corredor", valor: (item) => item.corredor || "N/A", form: (item) => item.corredor || "N/A" },
                      { columna: "cliente", label: "Cliente", form: (item) => item.cliente || item.idCliente?.nombre || "N/A" },
                      { columna: "po", label: labelPo, form: (item) => item.po || "N/A" },
                      { columna: "ecoTracto", label: "Eco. Tracto" },
                      { columna: "placasTracto", label: "Placas Tracto" },
                      { columna: "ecoChasis", label: "Eco. Chasis" },
                      { columna: "placasChasis", label: "Placas Chasis" },
                      { columna: "endDate", label: "Fin de Movimiento", form: (item) => { const f = item.endDateFormatted || item.endDate; if (!f) return "N/A"; const d = new Date(f); return isNaN(d.getTime()) ? String(f) : d.toLocaleString("es-MX"); } }
                    ]}
                    id="idGps"
                    paginacion={true}
                    elementosPorPagina={20}
                    csvname="GPS_Por_Recuperar"
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Grid.Col>
      </Grid.Row>
    </SiteWrapper>
  );
};

export default GpsPorRecuperarPage;
